import { askBlocker, matchNotice } from "@roof-hawk/core";
import { useRef, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import {
  askConversations,
  type Conversation,
  type ConversationQuestion,
  importConversation,
  readFileText,
} from "../../lib/conversations";

/**
 * The conversations that happened at this house.
 *
 * Recordings were meant to arrive by themselves (ADR 0064). They cannot yet —
 * Plaud's API for reading your own recordings is a private beta behind a
 * waitlist — so this is the way in that needs nobody's permission: the rep
 * opens the recording in Plaud, presses Share, and pastes the link here.
 *
 * One box takes both a link and the transcript itself. "Is this a URL" is a
 * question a computer can answer, and a screen that asks a rep to pick first is
 * a screen that gets it wrong on a phone in a driveway. The text path also has
 * to exist for its own sake: a share made without the transcript ticked, or made
 * invite-only, cannot be read at all, and "paste the words instead" is then the
 * difference between the feature working and not.
 *
 * A file goes through that same box (ADR 0113). Jonathon, 2026-09-07: "I just
 * need a place on the customer\'s job to upload a transcript so that way we can
 * go back to it and ask questions about the inspection/interaction with the
 * customer." Choosing a file reads its text and hands it to the same import, so
 * there is one path in and not two — and the asking is the box below it.
 */
export function ConversationsCard({
  jobId,
  repId,
  conversations,
  questions,
  onImported,
  onAsked,
}: {
  jobId: string;
  repId: string | null;
  conversations: Conversation[];
  questions: ConversationQuestion[];
  onImported: () => Promise<void>;
  onAsked: () => Promise<void>;
}) {
  const [paste, setPaste] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const add = async () => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const result = await importConversation({ jobId, repId, paste });
      setPaste("");
      setNote(
        result.partial
          ? t(
              "Added — but only part of it came across. Open it in Plaud to check what was shared.",
            )
          : result.fromLink
            ? t("Added from Plaud.")
            : t("Added."),
      );
      await onImported();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * A chosen file, read as text and sent down the same path as a paste.
   *
   * Text formats only. A .docx is a zip of XML and would arrive as mojibake
   * that passes every length check this screen has — better to say so than to
   * file a conversation made of nonsense.
   */
  const chooseFile = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const text = await readFileText(file);
      const result = await importConversation({ jobId, repId, paste: text });
      setNote(
        result.partial
          ? t("Added {name} — but there was not much in it. Check the file.", {
              name: file.name,
            })
          : t("Added {name}.", { name: file.name }),
      );
      await onImported();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  return (
    <div className="block">
      <ErrorNote error={error} />

      <div className="shelf">
        <b>{t("Add a transcript")}</b>
        <div>
          {t(
            "Upload the transcript file, or paste the words in. A Plaud share link works too — open the recording in Plaud, press Share, and paste the link.",
          )}
        </div>
        <div className="toolbar2">
          <input
            ref={fileInput}
            data-testid="transcript-file"
            type="file"
            accept=".txt,.md,.vtt,.srt,.text,text/plain,text/markdown"
            disabled={busy}
            onChange={(e) => void chooseFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <textarea
          rows={3}
          placeholder={t("…or paste the transcript, or a Plaud share link")}
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          disabled={busy}
        />
        <div className="toolbar2">
          <button
            className="addrow"
            style={{ marginTop: 0 }}
            disabled={busy || paste.trim() === ""}
            onClick={() => void add()}
          >
            {busy ? t("Reading…") : t("Add transcript")}
          </button>
          {note && <span className="count">{note}</span>}
        </div>
      </div>

      <AskPanel
        jobId={jobId}
        conversations={conversations}
        questions={questions}
        onAsked={onAsked}
      />

      {conversations.length === 0 ? (
        <div className="stub">
          <b>{t("No transcripts yet")}</b>
          {t("Nothing has been added to this job.")}
        </div>
      ) : (
        <div className="ewrap">
          {conversations.map((conversation) => (
            <ConversationRow
              key={conversation.id}
              conversation={conversation}
              open={open === conversation.id}
              onToggle={() =>
                setOpen(open === conversation.id ? null : conversation.id)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Ask this job's transcripts a question.
 *
 * The point of the whole card, in Jonathon's words: a place to put the
 * transcript "so that way we can go back to it and ask questions about the
 * inspection/interaction with the customer."
 *
 * Every answer is kept and listed under the box, newest first, because the
 * second person to wonder should read the first person's answer rather than
 * spend the key asking again. It also makes the answers checkable: an answer
 * that turns out to be wrong is still on screen next to the question that
 * produced it.
 *
 * The transcripts are not sent from here. `conversation-ask` reads them itself
 * as the caller — see the function, and ADR 0113.
 */
function AskPanel({
  jobId,
  conversations,
  questions,
  onAsked,
}: {
  jobId: string;
  conversations: Conversation[];
  questions: ConversationQuestion[];
  onAsked: () => Promise<void>;
}) {
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answer, setAnswer] = useState<string | null>(null);

  // Said before the button is pressed rather than after: "there is no
  // transcript on this job yet" is worth knowing while looking at the box.
  const blocker = askBlocker(question, conversations);

  const ask = async () => {
    setBusy(true);
    setError(null);
    setAnswer(null);
    try {
      const reply = await askConversations({ jobId, question });
      setAnswer(reply);
      setQuestion("");
      await onAsked();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shelf">
      <b>{t("Ask about this job")}</b>
      <div>
        {t(
          "Answered only from the transcripts above. If they do not say, it says so rather than guessing — what comes back here gets repeated to a customer.",
        )}
      </div>
      <ErrorNote error={error} />
      <textarea
        rows={2}
        placeholder={t("What did we tell them about the gutters?")}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        disabled={busy}
      />
      <div className="toolbar2">
        <button
          className="addrow"
          style={{ marginTop: 0 }}
          disabled={busy || blocker !== null}
          onClick={() => void ask()}
        >
          {busy ? t("Reading the transcripts…") : t("Ask")}
        </button>
        {question.trim() !== "" && blocker && (
          <span className="count">{t(blocker)}</span>
        )}
      </div>

      {answer && (
        <div className="convo-body">
          <b>{t("Answer")}</b>
          <pre className="convo-text">{answer}</pre>
        </div>
      )}

      {questions.length > 0 && (
        <div className="ewrap">
          {questions.map((asked) => (
            <div className="convo" key={asked.id}>
              <div className="convo-head">
                <b>{asked.question}</b>
                <span className="count">
                  {when(asked.created_at)}
                  {asked.asker?.full_name ? ` · ${asked.asker.full_name}` : ""}
                </span>
              </div>
              <div className="convo-body">
                <pre className="convo-text">{asked.answer}</pre>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConversationRow({
  conversation,
  open,
  onToggle,
}: {
  conversation: Conversation;
  open: boolean;
  onToggle: () => void;
}) {
  // core speaks the domain's language and the row speaks Postgres's, so the
  // translation happens here, once, in the open. Handing the row straight to
  // `matchNotice` reads fine and quietly loses `reviewed_at` — which would put
  // "verify" on every recording a person filed themselves.
  const notice = matchNotice({
    status: conversation.status,
    matchMethod: conversation.match_method,
    reviewedAt: conversation.reviewed_at,
  });

  return (
    <div className="convo">
      <button className="convo-head" onClick={onToggle}>
        <b>{conversation.name ?? t("Recording")}</b>
        <span className="count">
          {when(conversation.started_at)}
          {conversation.duration_ms !== null
            ? ` · ${minutes(conversation.duration_ms)}`
            : ""}
        </span>
        {notice && <span className="convo-guess">{t(notice)}</span>}
      </button>

      {open && (
        <div className="convo-body">
          {conversation.plaud_summary_md ? (
            <>
              <b>{t("Summary")}</b>
              <pre className="convo-text">{conversation.plaud_summary_md}</pre>
            </>
          ) : (
            // Said out loud rather than left blank: a missing summary usually
            // means the share was made without it, which is fixable in Plaud.
            <div className="stub">
              {t("No summary came with this recording.")}
            </div>
          )}

          {conversation.transcript_text ? (
            <>
              <b>{t("Transcript")}</b>
              <pre className="convo-text">{conversation.transcript_text}</pre>
            </>
          ) : (
            <div className="stub">
              {t("No transcript came with this recording.")}
            </div>
          )}

          {conversation.plaud_share_url && (
            <a
              href={conversation.plaud_share_url}
              target="_blank"
              rel="noreferrer"
            >
              {/* The audio is never copied out of Plaud — this is how anybody
                  listens to it. */}
              {t("Open in Plaud")}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function minutes(ms: number): string {
  const total = Math.round(ms / 60_000);
  return total < 1 ? t("under a minute") : t("{n} min", { n: total });
}
